import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAgents } from '@/hooks/useAgents';
import { useCreateSession, useStartSession } from '@/hooks/useSessions';
import { useCreateTemplate } from '@/hooks/useTemplates';
import { ApiError } from '@/api/client';
import { ErrorBanner, Loading } from '@/components/common/States';
import { isAgentOnline, shortId } from '@/lib/format';
import {
  CUSTOM_PROFILE,
  DSCP_OPTIONS,
  TRAFFIC_PROFILES,
  profileById,
  tosLabel,
} from '@/lib/trafficProfiles';
import type { Direction, Expectations, Protocol, StreamSpec, TemplateConfig, TestMode, Thresholds } from '@/types';

interface DraftStream {
  receiver_agent_id: string;
  profile: string; // traffic-profile id, or 'custom'
  protocol: Protocol;
  direction: Direction;
  bitrate_mbps: number;
  share_pct: number; // % of the declared link capacity (share mode only)
  packet_size: string; // kept as string for the optional input
  tos: number; // IP TOS byte (DSCP << 2); 0 = default
}

// How the operator allocates bandwidth across a traffic mix:
//   'mbps'  — absolute per-stream rate (the original behaviour)
//   'share' — percentage of a link capacity they declare, resolved to Mbps
//             before sending. Percentages are the natural way to express
//             "video should get 15% while bulk takes the rest", but the agent
//             paces in Mbps, so the conversion happens here rather than in the
//             backend, which has no way to know the link's capacity.
type AllocMode = 'mbps' | 'share';

const DEFAULT_PROFILE = 'video-stream';

// Fill a draft's transport params from a profile (or leave them for 'custom').
function withProfile(d: DraftStream, id: string): DraftStream {
  if (id === CUSTOM_PROFILE) return { ...d, profile: id };
  const p = profileById(id);
  if (!p) return { ...d, profile: id };
  return {
    ...d,
    profile: id,
    protocol: p.protocol,
    direction: p.direction,
    bitrate_mbps: p.bitrate_mbps,
    packet_size: p.packet_size ? String(p.packet_size) : '',
    tos: p.tos,
  };
}

const newStream = (): DraftStream =>
  withProfile(
    { receiver_agent_id: '', profile: '', protocol: 'tcp', direction: 'down', bitrate_mbps: 0, share_pct: 0, packet_size: '', tos: 0 },
    DEFAULT_PROFILE,
  );

export function NewSession() {
  const nav = useNavigate();
  const agents = useAgents();
  const createMut = useCreateSession();
  const startMut = useStartSession();
  const createTemplate = useCreateTemplate();

  const [label, setLabel] = useState('');
  const [testMode, setTestMode] = useState<TestMode>('ap_client');
  const [txAgent, setTxAgent] = useState('');
  const [streams, setStreams] = useState<DraftStream[]>([newStream()]);
  const [firmware, setFirmware] = useState('');
  const [tag, setTag] = useState('');
  const [durationSec, setDurationSec] = useState(''); // '' / '0' = unlimited
  const [thr, setThr] = useState<Record<string, string>>({
    min_throughput_mbps: '',
    max_latency_ms: '',
    max_loss_pct: '',
    max_jitter_ms: '',
    max_retransmits: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [allocMode, setAllocMode] = useState<AllocMode>('mbps');
  // What the link is SUPPOSED to negotiate. Feeds the analysis engine, not the
  // pass/fail verdict — an unmet expectation is a finding, not a failed SLA.
  const [exp, setExp] = useState<Record<string, string>>({
    wifi_standard: '',
    wifi_security: '',
    wifi_band: '',
  });
  const [linkCapacity, setLinkCapacity] = useState(''); // Mbps, share mode only
  const setThreshold = (k: string, v: string) => setThr((t) => ({ ...t, [k]: v }));

  const busy = createMut.isPending || startMut.isPending;
  const agentList = agents.data ?? [];

  const setStream = (i: number, patch: Partial<DraftStream>) =>
    setStreams((s) => s.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const capacity = Number(linkCapacity) || 0;
  const shareTotal = streams.reduce((sum, s) => sum + (Number(s.share_pct) || 0), 0);

  // Percentage → Mbps. Only meaningful in share mode with a declared capacity;
  // everywhere else the operator's absolute figure wins untouched.
  const resolveBitrate = (s: DraftStream): number => {
    if (allocMode !== 'share' || capacity <= 0) return Number(s.bitrate_mbps) || 0;
    const pct = Number(s.share_pct) || 0;
    if (pct <= 0) return 0; // 0% = unlimited, same convention as 0 Mbps
    return Math.max(1, Math.round((capacity * pct) / 100));
  };

  // Build the shared config (streams + thresholds) used by both launch and
  // "save as template". Returns null (after setting an error) when invalid.
  function buildConfig(): {
    specs: StreamSpec[];
    thresholds: Thresholds | null;
    expectations: Expectations | null;
  } | null {
    if (!txAgent) {
      setError('Pick a transmitter agent.');
      return null;
    }
    if (streams.some((s) => !s.receiver_agent_id)) {
      setError('Every stream needs a receiver agent.');
      return null;
    }
    if (allocMode === 'share') {
      if (capacity <= 0) {
        setError('Share mode needs a link capacity to take a percentage of.');
        return null;
      }
      if (shareTotal > 100) {
        setError(`Shares add up to ${shareTotal}% — they can't exceed 100%.`);
        return null;
      }
    }
    const specs: StreamSpec[] = streams.map((s) => ({
      receiver_agent_id: s.receiver_agent_id,
      protocol: s.protocol,
      direction: s.direction,
      // In share mode the operator allocates a percentage of the link they
      // declared; resolve it to the Mbps the agent actually paces at. 0 stays 0
      // (unlimited) in both modes — a share of "unlimited" is meaningless.
      bitrate_mbps: resolveBitrate(s),
      ...(s.packet_size ? { packet_size: Number(s.packet_size) } : {}),
      ...(s.tos ? { tos: s.tos } : {}),
      // Records which application this stream emulates. The backend's analysis
      // engine needs the intent, not just the DSCP byte.
      ...(s.profile ? { traffic_type: s.profile } : {}),
    }));
    const t: Record<string, number> = {};
    for (const [k, v] of Object.entries(thr)) if (v !== '') t[k] = Number(v);
    const thresholds = Object.keys(t).length > 0 ? (t as Thresholds) : null;
    const e: Record<string, string> = {};
    for (const [k, v] of Object.entries(exp)) if (v !== '') e[k] = v;
    const expectations = Object.keys(e).length > 0 ? (e as Expectations) : null;
    return { specs, thresholds, expectations };
  }

  async function saveAsTemplate() {
    setError(null);
    const cfg = buildConfig();
    if (!cfg) return;
    const tname = prompt('Template name?');
    if (!tname?.trim()) return;
    const config: TemplateConfig = {
      label: label || null,
      test_mode: testMode,
      firmware: firmware || null,
      tag: tag || null,
      thresholds: cfg.thresholds,
      expectations: cfg.expectations,
      transmitter_agent_id: txAgent,
      streams: cfg.specs,
    };
    try {
      await createTemplate.mutateAsync({ name: tname.trim(), config });
      nav('/templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template.');
    }
  }

  async function launch() {
    setError(null);
    const cfg = buildConfig();
    if (!cfg) return;
    const specs = cfg.specs;
    const thresholds = cfg.thresholds ?? {};
    const hasThr = cfg.thresholds !== null;

    try {
      const session = await createMut.mutateAsync({
        label: label || null,
        test_mode: testMode,
        firmware: firmware || null,
        tag: tag || null,
        thresholds: hasThr ? thresholds : null,
        expectations: cfg.expectations,
      });
      const started = await startMut.mutateAsync({
        id: session.id,
        req: {
          transmitter_agent_id: txAgent,
          streams: specs,
          duration_seconds: Math.max(0, Number(durationSec) || 0),
        },
      });
      nav(`/sessions/${started.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setError('Traffic balance is empty — top up before starting a test.');
      } else if (err instanceof ApiError && err.status === 504) {
        const reason = (err.body as { reason?: string })?.reason;
        setError(`Orchestration failed${reason ? `: ${reason}` : ''}. Are both agents online?`);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to start test.');
      }
    }
  }

  if (agents.isLoading) return <Loading label="Loading agents" />;

  const agentOption = (placeholder: string) => (
    <>
      <option value="">{placeholder}</option>
      {agentList.map((a) => (
        <option key={a.id} value={a.id}>
          {(a.label || shortId(a.external_id)) + (isAgentOnline(a.last_seen_at) ? ' ● online' : ' ○ offline')}
        </option>
      ))}
    </>
  );

  return (
    <div className="fade-up" style={{ maxWidth: 880 }}>
      <div className="page-head">
        <div className="eyebrow">configure</div>
        <div className="title">New test</div>
        <div className="sub">Define the transmitter, receivers and per-stream parameters.</div>
      </div>

      {agentList.length < 2 && (
        <div className="card" style={{ marginBottom: 18, borderColor: 'rgba(255,194,75,0.3)' }}>
          <div className="mono" style={{ fontSize: 13, color: 'var(--warn)' }}>
            ▸ You need at least two enrolled agents (one transmitter, one receiver). Enroll agents with
            <span className="mono"> agent login</span>.
          </div>
        </div>
      )}

      <div className="card" style={{ display: 'grid', gap: 18, marginBottom: 18 }}>
        <div className="row wrap" style={{ gap: 18 }}>
          <div className="field grow" style={{ minWidth: 220 }}>
            <label className="label">Label (optional)</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. AP-7 downlink sweep" />
          </div>
          <div className="field" style={{ minWidth: 180 }}>
            <label className="label">Test mode</label>
            <select className="select" value={testMode} onChange={(e) => setTestMode(e.target.value as TestMode)}>
              <option value="ap_client">ap_client</option>
              <option value="mesh">mesh</option>
            </select>
          </div>
          <div className="field grow" style={{ minWidth: 220 }}>
            <label className="label">Transmitter agent</label>
            <select className="select" value={txAgent} onChange={(e) => setTxAgent(e.target.value)}>
              {agentOption('— select transmitter —')}
            </select>
          </div>
        </div>
        <div className="row wrap" style={{ gap: 18 }}>
          <div className="field" style={{ minWidth: 200 }}>
            <label className="label">Firmware (optional)</label>
            <input className="input" value={firmware} onChange={(e) => setFirmware(e.target.value)} placeholder="e.g. v2.1.3" />
          </div>
          <div className="field grow" style={{ minWidth: 200 }}>
            <label className="label">Tag (optional)</label>
            <input className="input" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. nightly, robot-slot-1" />
          </div>
          <div className="field" style={{ minWidth: 170 }}>
            <label className="label">Duration (seconds)</label>
            <input
              className="input"
              type="number"
              min={0}
              value={durationSec}
              onChange={(e) => setDurationSec(e.target.value)}
              placeholder="0 = until stopped"
            />
            <div className="mono" style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              {Number(durationSec) > 0 ? `auto-stops after ${Number(durationSec)}s` : 'runs until you stop it'}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>expected link (optional)</div>
        <div className="sub" style={{ marginBottom: 12, fontSize: 12 }}>
          What this link is supposed to negotiate. Unlike thresholds these don't pass or fail the
          run — they let the analysis call out a silent mismatch, e.g. "advertised Wi-Fi 7,
          negotiated 11ax". Needs an agent that reports Wi-Fi context (phones do; most desktops
          don't).
        </div>
        <div className="row wrap" style={{ gap: 14 }}>
          {[
            { k: 'wifi_standard', label: 'Standard', opts: ['', '11ac', '11ax', '11be'] },
            { k: 'wifi_security', label: 'Security', opts: ['', 'WPA2', 'WPA3', 'OWE', 'OPEN'] },
            { k: 'wifi_band', label: 'Band', opts: ['', '2.4GHz', '5GHz', '6GHz'] },
          ].map(({ k, label: l, opts }) => (
            <div className="field" style={{ width: 150 }} key={k}>
              <label className="label">{l}</label>
              <select
                className="select"
                value={exp[k]}
                onChange={(e) => setExp((x) => ({ ...x, [k]: e.target.value }))}
              >
                {opts.map((o) => (
                  <option key={o} value={o}>{o === '' ? '— any —' : o}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>pass / fail thresholds (optional)</div>
        <div className="sub" style={{ marginBottom: 12, fontSize: 12 }}>
          Only the fields you fill in are checked. The run PASSES when all set thresholds pass.
        </div>
        <div className="row wrap" style={{ gap: 14 }}>
          {[
            { k: 'min_throughput_mbps', label: 'Min throughput (Mbps)' },
            { k: 'max_latency_ms', label: 'Max latency (ms)' },
            { k: 'max_loss_pct', label: 'Max loss (%)' },
            { k: 'max_jitter_ms', label: 'Max jitter (ms)' },
            { k: 'max_retransmits', label: 'Max retransmits' },
          ].map(({ k, label: l }) => (
            <div className="field" style={{ width: 150 }} key={k}>
              <label className="label">{l}</label>
              <input
                className="input"
                type="number"
                min={0}
                placeholder="—"
                value={thr[k]}
                onChange={(e) => setThreshold(k, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="spread" style={{ marginBottom: 10 }}>
        <div className="eyebrow">traffic mix ({streams.length} stream{streams.length === 1 ? '' : 's'})</div>
        <button className="btn" onClick={() => setStreams((s) => [...s, newStream()])} disabled={streams.length >= 64}>
          + Add stream
        </button>
      </div>

      {/* Bandwidth allocation. Streams run CONCURRENTLY, which is the whole
          point — prioritisation and fairness problems only appear when traffic
          classes contend for the same air at the same time. */}
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div className="row wrap" style={{ gap: 14, alignItems: 'flex-end' }}>
          <div className="field" style={{ width: 190 }}>
            <label className="label">Allocate bandwidth by</label>
            <select className="select" value={allocMode} onChange={(e) => setAllocMode(e.target.value as AllocMode)}>
              <option value="mbps">Absolute Mbps</option>
              <option value="share">Share of link (%)</option>
            </select>
          </div>
          {allocMode === 'share' && (
            <>
              <div className="field" style={{ width: 160 }}>
                <label className="label">Link capacity (Mbps)</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  placeholder="e.g. 600"
                  value={linkCapacity}
                  onChange={(e) => setLinkCapacity(e.target.value)}
                />
              </div>
              <div className="mono" style={{ fontSize: 12, color: shareTotal > 100 ? 'var(--bad, #d33)' : 'var(--muted)', paddingBottom: 8 }}>
                allocated {shareTotal}% {shareTotal > 100 ? '— over 100%' : `of ${capacity || '—'} Mbps`}
              </div>
            </>
          )}
        </div>
        {allocMode === 'share' && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
            Percentages resolve to Mbps against the capacity you declare here — the agents pace in
            Mbps, and only you know what this link is actually capable of. Leave a stream at 0% to
            let it run unlimited (useful for the bulk stream that should soak up whatever is left).
          </div>
        )}
      </div>

      <div className="stack" style={{ gap: 12, marginBottom: 18 }}>
        {streams.map((s, i) => {
          const custom = s.profile === CUSTOM_PROFILE;
          const prof = profileById(s.profile);
          return (
            <div className="card" key={i} style={{ padding: 16 }}>
              <div className="row wrap" style={{ gap: 14, alignItems: 'flex-end' }}>
                <div className="field grow" style={{ minWidth: 200 }}>
                  <label className="label">Receiver agent</label>
                  <select className="select" value={s.receiver_agent_id} onChange={(e) => setStream(i, { receiver_agent_id: e.target.value })}>
                    {agentOption('— select receiver —')}
                  </select>
                </div>
                <div className="field" style={{ minWidth: 190 }}>
                  <label className="label">Traffic type</label>
                  <select
                    className="select"
                    value={s.profile}
                    onChange={(e) => setStreams((arr) => arr.map((row, idx) => (idx === i ? withProfile(row, e.target.value) : row)))}
                  >
                    {TRAFFIC_PROFILES.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                    <option value={CUSTOM_PROFILE}>Custom…</option>
                  </select>
                </div>
                {streams.length > 1 && (
                  <button
                    className="btn btn-ghost"
                    style={{ color: 'var(--loss)' }}
                    onClick={() => setStreams((arr) => arr.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Share input lives outside the custom/preset split — allocating
                  a percentage is orthogonal to whether the transport params
                  came from a profile. */}
              {allocMode === 'share' && (
                <div className="row" style={{ gap: 14, alignItems: 'flex-end', marginTop: 12 }}>
                  <div className="field" style={{ width: 140 }}>
                    <label className="label">Share of link (%)</label>
                    <input
                      className="input"
                      type="number"
                      min={0}
                      max={100}
                      value={s.share_pct}
                      onChange={(e) => setStream(i, { share_pct: Number(e.target.value) })}
                    />
                  </div>
                  <div className="mono" style={{ fontSize: 12, color: 'var(--muted)', paddingBottom: 8 }}>
                    → {resolveBitrate(s) === 0 ? 'unlimited' : `${resolveBitrate(s)} Mbps`}
                  </div>
                </div>
              )}

              {!custom ? (
                <div className="mono" style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
                  ▸ {s.protocol} · {s.direction} ·{' '}
                  {resolveBitrate(s) === 0 ? 'unlimited' : `${resolveBitrate(s)} Mbps`}
                  {s.packet_size ? ` · ${s.packet_size}B` : ''} · {tosLabel(s.tos)}
                  {prof ? <span style={{ marginLeft: 8, opacity: 0.8 }}>— {prof.hint}</span> : null}
                </div>
              ) : (
                <div className="row wrap" style={{ gap: 14, alignItems: 'flex-end', marginTop: 12 }}>
                  <div className="field" style={{ width: 100 }}>
                    <label className="label">Protocol</label>
                    <select className="select" value={s.protocol} onChange={(e) => setStream(i, { protocol: e.target.value as Protocol })}>
                      <option value="tcp">tcp</option>
                      <option value="udp">udp</option>
                    </select>
                  </div>
                  <div className="field" style={{ width: 110 }}>
                    <label className="label">Direction</label>
                    <select className="select" value={s.direction} onChange={(e) => setStream(i, { direction: e.target.value as Direction })}>
                      <option value="down">down</option>
                      <option value="up">up</option>
                      <option value="both">both</option>
                    </select>
                  </div>
                  {/* In share mode the percentage above drives the rate — showing
                      a second, contradicting rate box would be a trap. */}
                  {allocMode === 'mbps' && (
                    <div className="field" style={{ width: 120 }}>
                      <label className="label">Mbps (0=max)</label>
                      <input className="input" type="number" min={0} value={s.bitrate_mbps} onChange={(e) => setStream(i, { bitrate_mbps: Number(e.target.value) })} />
                    </div>
                  )}
                  <div className="field" style={{ width: 110 }}>
                    <label className="label">Pkt size</label>
                    <input className="input" type="number" min={64} placeholder="auto" value={s.packet_size} onChange={(e) => setStream(i, { packet_size: e.target.value })} />
                  </div>
                  <div className="field" style={{ minWidth: 190 }}>
                    <label className="label">QoS tag (DSCP)</label>
                    <select className="select" value={s.tos} onChange={(e) => setStream(i, { tos: Number(e.target.value) })}>
                      {DSCP_OPTIONS.map((o) => (
                        <option key={o.tos} value={o.tos}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div style={{ marginBottom: 16 }}><ErrorBanner error={error} /></div>}

      <div className="row" style={{ gap: 12 }}>
        <button className="btn btn-primary" onClick={launch} disabled={busy}>
          {busy ? <span className="spinner" /> : 'Launch test →'}
        </button>
        <button className="btn" onClick={saveAsTemplate} disabled={busy || createTemplate.isPending}>
          {createTemplate.isPending ? 'Saving…' : 'Save as template'}
        </button>
        <button className="btn btn-ghost" onClick={() => nav('/sessions')} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
